/**
 * The edit model as it crosses the API and sits in the database: a size
 * limit and a strict shape check. The renderer checks every value again
 * against the original (`render.ts`), so this is only about refusing
 * what is not a model at all: an unknown version, unknown keys, wrong
 * types, or something far too large.
 */
import {
  MAX_SECTION_NAME_LENGTH,
  SECTION_MODEL_VERSION,
  type OrderEntry,
  type SectionEditModel,
  type SectionLayout,
  type SlotEdit,
  type UnmappedEdit,
} from './model.js';
import { MAX_ATTR_LENGTH, MAX_TEXT_LENGTH } from './text.js';

/** Upper limit for the stored model as JSON (UTF-8 bytes). A move, a hide and a duplicate on the real newsletter are 365 bytes. */
export const MAX_SECTION_MODEL_BYTES = 64 * 1024;

const MAX_ID_LENGTH = 80;
const MAX_ENTRIES = 2000;

export type ParsedSectionModel =
  | { ok: true; model: SectionEditModel; bytes: number }
  | {
      ok: false;
      error: { code: 'not_a_model' | 'unknown_version' | 'too_large'; message: string };
    };

function refuse(
  code: 'not_a_model' | 'unknown_version' | 'too_large',
  message: string,
): ParsedSectionModel {
  return { ok: false, error: { code, message } };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function onlyKeys(value: Record<string, unknown>, allowed: readonly string[]): boolean {
  return Object.keys(value).every((key) => allowed.includes(key));
}

function isId(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= MAX_ID_LENGTH;
}

function isString(value: unknown, max: number): value is string {
  return typeof value === 'string' && value.length <= max;
}

function isSlotEdit(value: unknown): value is SlotEdit {
  if (!isRecord(value) || !onlyKeys(value, ['text', 'src', 'alt', 'href', 'o'])) return false;
  return (
    (value['text'] === undefined || isString(value['text'], MAX_TEXT_LENGTH)) &&
    (value['src'] === undefined || isString(value['src'], MAX_ATTR_LENGTH)) &&
    (value['alt'] === undefined || isString(value['alt'], MAX_ATTR_LENGTH)) &&
    (value['href'] === undefined || isString(value['href'], MAX_ATTR_LENGTH)) &&
    (value['o'] === undefined || value['o'] === 1)
  );
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 999_999;
}

function isOrderEntry(value: unknown): value is OrderEntry {
  if (!isRecord(value) || !onlyKeys(value, ['s', 'd', 'h', 'x'])) return false;
  return (
    isId(value['s']) &&
    (value['d'] === undefined || isCount(value['d'])) &&
    (value['h'] === undefined || value['h'] === 1) &&
    (value['x'] === undefined || value['x'] === 1)
  );
}

function isLayout(value: unknown): value is SectionLayout {
  if (!isRecord(value) || !onlyKeys(value, ['cut', 'order', 'n'])) return false;
  const { cut, order, n } = value;
  return (
    Array.isArray(cut) &&
    cut.length <= MAX_ENTRIES &&
    cut.every(isId) &&
    Array.isArray(order) &&
    order.length <= MAX_ENTRIES &&
    order.every(isOrderEntry) &&
    isCount(n)
  );
}

/**
 * Checks a model that came from a client or from storage. Accepts the
 * parsed JSON value. Refuses another version than
 * {@link SECTION_MODEL_VERSION}, unknown keys, wrong types and a model
 * over {@link MAX_SECTION_MODEL_BYTES}.
 */
export function parseSectionModel(input: unknown): ParsedSectionModel {
  const shape = 'The section edits are not readable.';
  if (!isRecord(input)) return refuse('not_a_model', shape);
  if (input['v'] !== SECTION_MODEL_VERSION) {
    return refuse(
      'unknown_version',
      'These section edits were made by another version of the editor. Reload the page and try again.',
    );
  }
  let json: string;
  try {
    json = JSON.stringify(input);
  } catch {
    return refuse('not_a_model', shape);
  }
  const bytes = new TextEncoder().encode(json).length;
  if (bytes > MAX_SECTION_MODEL_BYTES) {
    return refuse(
      'too_large',
      `The section edits are ${(bytes / 1024).toFixed(1)} KB, over the limit of ${MAX_SECTION_MODEL_BYTES / 1024} KB.`,
    );
  }
  if (!onlyKeys(input, ['v', 'slots', 'layout', 'names'])) return refuse('not_a_model', shape);

  const { slots, layout, names } = input;
  if (slots !== undefined) {
    if (!isRecord(slots)) return refuse('not_a_model', shape);
    const entries = Object.entries(slots);
    if (entries.length > MAX_ENTRIES) return refuse('not_a_model', shape);
    for (const [id, edit] of entries) {
      if (!isId(id) || !isSlotEdit(edit)) return refuse('not_a_model', shape);
    }
  }
  if (layout !== undefined && !isLayout(layout)) return refuse('not_a_model', shape);
  if (names !== undefined) {
    if (!isRecord(names)) return refuse('not_a_model', shape);
    const entries = Object.entries(names);
    if (entries.length > MAX_ENTRIES) return refuse('not_a_model', shape);
    for (const [id, name] of entries) {
      if (!isId(id) || !isString(name, MAX_SECTION_NAME_LENGTH)) {
        return refuse('not_a_model', shape);
      }
    }
  }
  return { ok: true, model: input as unknown as SectionEditModel, bytes };
}

/**
 * The model without the slot edits and names that `unmapped` reports as
 * lost. Layout issues (`cut_changed`, `order_reset`) drop the stored
 * layout, which is what the renderer already does with it.
 */
export function dropUnmappedEdits(
  model: SectionEditModel,
  unmapped: readonly UnmappedEdit[],
): SectionEditModel {
  const next: SectionEditModel = { v: model.v };
  const lostSlots = new Set(
    unmapped.filter((u) => u.code === 'slot_missing' || u.code === 'slot_refused').map((u) => u.id),
  );
  const lostNames = new Set(unmapped.filter((u) => u.code === 'name_missing').map((u) => u.id));
  const layoutLost = unmapped.some((u) => u.code === 'cut_changed' || u.code === 'order_reset');

  if (model.slots) {
    const kept = Object.entries(model.slots).filter(([id]) => !lostSlots.has(id));
    if (kept.length > 0) next.slots = Object.fromEntries(kept);
  }
  if (model.layout && !layoutLost) next.layout = model.layout;
  if (model.names) {
    const kept = Object.entries(model.names).filter(([id]) => !lostNames.has(id));
    if (kept.length > 0) next.names = Object.fromEntries(kept);
  }
  return next;
}
